import { useEffect, useRef, useState } from "react";
import { requestWorkspaceMode } from "@/lib/workspaceModeNavigation";
import {
  columnSourceNote,
  MATERIALS_SOURCE_NOTE,
  type ColumnSourceNote,
} from "@/lib/manufacturing/cogsColumnSource";
import type { MfgPricingLayerColumn } from "@/lib/manufacturing/types";

type Placement = "left" | "right";

function SourcePopover({
  label,
  note,
  placement,
}: {
  label: string;
  note: ColumnSourceNote;
  placement: Placement;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [box, setBox] = useState<{ top: number; left: number; right: number } | null>(null);

  useEffect(() => {
    if (!open) return;
    function onMouseDown(event: MouseEvent) {
      const root = rootRef.current;
      if (!root) return;
      if (event.target instanceof Node && root.contains(event.target)) return;
      setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onMouseDown, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onMouseDown, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <span ref={rootRef} className="relative inline-flex normal-case tracking-normal">
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-expanded={open}
        title={note.formula ? `${note.detail} ${note.formula}` : note.detail}
        className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-current/30 bg-white/80 text-[10px] font-semibold leading-none text-inherit hover:border-indigo-500 hover:text-indigo-700 dark:bg-zinc-900/80 dark:hover:text-indigo-300"
        onClick={() => {
          const rect = buttonRef.current?.getBoundingClientRect();
          if (rect) setBox({ top: rect.bottom + 4, left: rect.left, right: window.innerWidth - rect.right });
          setOpen((value) => !value);
        }}
      >
        !
      </button>
      {open && box ? (
        <span
          role="dialog"
          aria-label={label}
          className="fixed z-50 w-64 rounded-md border border-gray-200 bg-white p-2.5 text-left text-[11px] font-normal normal-case leading-relaxed tracking-normal text-gray-700 shadow-lg dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
          style={
            placement === "left"
              ? { top: box.top, right: box.right }
              : { top: box.top, left: Math.min(box.left, window.innerWidth - 272) }
          }
        >
          <span className="block font-semibold text-gray-900 dark:text-zinc-100">{note.title}</span>
          <span className="mt-1 block">{note.detail}</span>
          {note.formula ? <span className="mt-1 block font-medium">{note.formula}</span> : null}
          {note.link ? (
            <button
              type="button"
              className="mt-2 font-semibold text-indigo-700 hover:underline dark:text-indigo-300"
              onClick={() => {
                setOpen(false);
                requestWorkspaceMode({ mode: note.link!.mode });
              }}
            >
              {note.link.label}
            </button>
          ) : null}
        </span>
      ) : null}
    </span>
  );
}

export function PricingColumnSourceHelp({
  column,
  placement = "right",
}: {
  column: MfgPricingLayerColumn;
  placement?: Placement;
}) {
  const note = columnSourceNote(column);
  return <SourcePopover label={`Where ${column.label} comes from`} note={note} placement={placement} />;
}

export function MaterialsSourceHelp() {
  return <SourcePopover label="Where materials comes from" note={MATERIALS_SOURCE_NOTE} placement="right" />;
}
